import { Button, ButtonGroup, Checkbox, Modal, Link, Text } from '@nike/eds';
import { FC, useState, useEffect } from 'react';
import { useI18nContext } from '../../../context/language/use-i18n-context';
import { PassApprovalDetail, ChangeMember } from 'models/IPassApprovalDetail';
import { formatDates, getCurrencySymbol } from '../../../utils/date-helper';
import { PassApproval } from 'models/IPassApprovals';
import { AppConfig } from '../../../utils/constants';
import { Languages } from 'models/translations';
import { useUserContext } from '../../../context/user';
import { useLanguageContext } from 'context/language/context';
import React from 'react';
import ts from './i18n';
import './approval-detail-modal.css';

export interface ApprovalDetailProps {
  passApproval: PassApproval;
  passApprovaldetail: PassApprovalDetail;
  showDetail: boolean;
  subHeader: string;
  funOnDismiss: () => void;
  handleAction: (id: number, actionType: 'approve' | 'decline') => void;
}

export const ApprovalDetail: FC<ApprovalDetailProps> = ({
  passApproval,
  passApprovaldetail,
  showDetail,
  subHeader,
  funOnDismiss,
  handleAction,
}) => {
  const { i18nString } = useI18nContext();
  const [language] = useLanguageContext();
  const userInfo = useUserContext();
  const [passApprovalDetail, setPassApprovalDetail] = useState<PassApprovalDetail>();

  const initialState = {
    modalOpen: true,
    altColumn: '',
    displayVendorCompMembers: false,
    companyName: '',
    requestDates: '',
    compAmount: '',
    changeTypeEdit: false,
    isChineseVendor: false,
    isComp: passApproval?.approvalTypeId === AppConfig.visitTypes.comp,
    isVendor: passApproval?.approvalTypeId === AppConfig.visitTypes.vendor,
    isFamily: passApproval?.approvalTypeId === AppConfig.visitTypes.family,
    isGuest: passApproval?.approvalTypeId === AppConfig.visitTypes.guest,
    visitorsText:
      passApproval.visitors > 1
        ? i18nString(ts.APPROVAL_VISITORS)
        : i18nString(ts.APPROVAL_VISITOR),
    submitterText: i18nString(ts.APPROVAL_REQUESTED_BY),
    visitorHeading: i18nString(ts.APPROVAL_VISITOR_HEADING),
    altHeading:
      passApproval.approvalTypeId === AppConfig.visitTypes.family
        ? i18nString(ts.APPROVAL_RELATIONSHIP)
        : i18nString(ts.APPROVAL_STORE),
    currentTitle: i18nString(ts.APPROVAL_CURRENT),
    editedTitle: i18nString(ts.APPROVAL_EDITED),
    chinaVendorChecked: false,
    isChinese: userInfo.passplayUser?.culture === AppConfig.locales['china'], // Need to check the logic once
  };

  const [state, setState] = useState(initialState);

  useEffect(() => {
    const fetchApprovalDetail = async () => {
      const detail = passApprovaldetail;
      setPassApprovalDetail(detail);

      const newState = { ...initialState };

      if (
        detail?.shoppingDateStart &&
        passApproval?.approvalTypeId === AppConfig.visitTypes.vendor
      ) {
        newState.altColumn = detail.passLocation;
        newState.displayVendorCompMembers = true;
        newState.companyName = detail.organization;
        newState.requestDates = formatDates(
          detail.shoppingDateStart,
          detail.shoppingDateEnd,
          null,
          detail.shoppingDateEnd,
          null,
        );
      } else if (detail && passApproval?.approvalTypeId === AppConfig.visitTypes.comp) {
        newState.displayVendorCompMembers = true;
        newState.companyName = detail.organization;
        newState.requestDates = formatDates(
          detail.shoppingDateStart,
          detail.shoppingDateEnd,
          null,
          detail.shoppingDateEnd,
          null,
        );
        newState.compAmount = getCurrencySymbol(detail.passLocationCountryCode) + detail.compAmount;
      } else {
        newState.changeTypeEdit = detail?.changeType === 'Edit';
        newState.altColumn = detail?.passLocation || '';
      }
      newState.isChineseVendor = detail.passLocationCountryCode === 156 && initialState.isVendor;

      setState(newState);
    };

    fetchApprovalDetail();
  }, [passApproval, i18nString]);

  const formatName = (firstName: string, lastName: string) => {
    return language === Languages.JA ? `${lastName} ${firstName}` : `${firstName} ${lastName}`;
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prevState => ({
      ...prevState,
      chinaVendorChecked: event.target.checked,
    }));
  };

  const Header = () => (
    <div className="my-approvals-modal-header">
      <Text as="h3" className="my-approvals-modal-heading" data-testid="appDetailMainHeader">
        {formatName(passApproval.firstName, passApproval.lastName)}
      </Text>
      <Text as="h5" className="my-approvals-modal-subheading" data-testid="appDetailSubHeader">
        {subHeader}
      </Text>
    </div>
  );

  const renderList = (changedMembers: ChangeMember[], isFamily: boolean, edited = false) => (
    <div className="my-approvals-list" data-ui="approvalsList" data-testid="appDetailApprovalsList">
      <div className="list">
        <div className="list__header" data-testid="appDetailApprovalsListHeader">
          <h4>{state.visitorHeading}</h4>
          {isFamily && <h4>{state.altHeading}</h4>}
        </div>
        <div className="list__body" data-testid="appDetailApprovalsListBody">
          {changedMembers
            .sort((a, b) => a.firstName.localeCompare(b.firstName))
            .map(change => (
              <div className="list__row" key={change.firstName} data-ui={change.firstName}>
                <h4 className="txt_uppercase">{formatName(change.firstName, change.lastName)}</h4>
                {isFamily && (
                  <div>
                    {edited
                      ? i18nString(ts[change.editedRelationshipToken as keyof typeof ts])
                      : i18nString(ts[change.relationshipToken as keyof typeof ts])}
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );

  const vendorAgreement = (
    <div data-testid="appDetailLegalText" className="eds-spacing--mb-16 eds-type--legal-1">
      {!state.isChinese ? (
        <Text data-testid="appDetailLegalText-En">
          * It is against NIKE policy to approve a Vendor pass meant for the benefit of a government
          official, a government organization, a state owned entity or any person working in such
          Government organization or state owned entity. By approving the Vendor pass you confirm
          having read, understood and complied with the
          <Link href="https://nike.sharepoint.com/teams/ap5/NikeGCLegal/PublishingImages/Lists/PoliciesList/EditForm/Mainland%20China%20Rider%20on%20Government%20Officials%20Gifts%20and%20Hospitality%20-%20EN.PDF">
            {' '}
            Mainland China Rider to &quot;Government Official&quot; Gifts and Hospitality Policy
          </Link>
          . Please contact{' '}
          <Link href="mailto:GC.ECO@NIKE.COM">Legal Compliance Ethics &amp; Compliance Office</Link>
          , if you have any questions or concerns.
        </Text>
      ) : (
        <Text data-testid="appDetailLegalText-zh">
          *
          公司政策禁止将供应商通行证发放给政府官员或在政府机构及国有企业内任职的员工，批准该供应商通行证的申请表明你确认你已阅读、理解并遵守公司《
          <Link href="https://nike.sharepoint.com/teams/ap5/NikeGCLegal/PublishingImages/Lists/PoliciesList/EditForm/Mainland%20China%20Rider%20on%20Government%20Officials%20Gifts%20and%20Hospitality%20-%20CN.PDF">
            {' '}
            中国大陆&quot;政府官员&quot;礼品及费用政策
          </Link>
          》的规定。 如有任何疑问或顾虑，请联系
          <Link href="mailto:GC.ECO@NIKE.COM">道德与合规办公室</Link>。
        </Text>
      )}
    </div>
  );

  const Footer = () => (
    <>
      <div>
        {state.isChineseVendor && (
          <Checkbox
            data-testid="appDetailLegalCheckbox"
            id="vendorCheck"
            name="Jordan"
            label={vendorAgreement}
            onChange={handleCheckboxChange}
            checked={state.chinaVendorChecked}
          />
        )}
      </div>
      <div className="my-approvals-modal-footer">
        <ButtonGroup>
          <Button
            id="decline__button"
            onClick={() => handleAction(passApproval?.id, 'decline')}
            data-testid="appDetailDecline-button"
          >
            {i18nString(ts.APPROVAL_DECLINE)}
          </Button>
          <Button
            id="approve__button"
            onClick={() => handleAction(passApproval?.id, 'approve')}
            data-testid="appDetailApprove-button"
            disabled={state.isChineseVendor && !state.chinaVendorChecked}
          >
            {i18nString(ts.APPROVAL_APPROVE)}
          </Button>
        </ButtonGroup>
      </div>
    </>
  );

  return (
    <Modal
      onDismiss={funOnDismiss}
      isOpen={showDetail}
      headerSlot={<Header />}
      footerSlot={<Footer />}
      hideFade
      id="modalApprovalDetail"
      data-testid="appDetailAproval-modal"
    >
      <div>
        {passApprovalDetail && state.displayVendorCompMembers && (
          <div className="hero my-approvals-modal-hero my-approvals-new">
            {state.companyName && <div>{state.companyName}</div>}
            {passApproval.visitors !== null && state.isVendor && (
              <div>{passApproval.visitors + ' ' + state.visitorsText}</div>
            )}
            {state.requestDates && <div>{state.requestDates}</div>}
            {passApproval.submitterName && state.isVendor && (
              <div>{state.submitterText + ' ' + passApproval.submitterName}</div>
            )}
            {state.isVendor && <div>{passApprovalDetail.passLocation}</div>}
            {state.compAmount && <div>{state.compAmount}</div>}
          </div>
        )}
        {state.changeTypeEdit && state.isFamily && (
          <div className="hero my-approvals-modal-hero my-approvals-edited">
            <div>{state.editedTitle}</div>
          </div>
        )}
        {passApprovalDetail && renderList(passApprovalDetail.changedMembers, state.isFamily)}

        {state.changeTypeEdit && state.isFamily && (
          <div className="hero my-approvals-modal-hero my-approvals-edited my-approvals-current">
            <div>{state.currentTitle}</div>
          </div>
        )}

        {state.changeTypeEdit &&
          state.isFamily &&
          passApprovalDetail &&
          renderList(passApprovalDetail.changedMembers, state.isFamily, true)}
      </div>
    </Modal>
  );
};

export default ApprovalDetail;
